import React, { useState, createElement } from 'react';
import { StatsCard } from './shared/StatsCard';
import { SegmentDropdown } from './shared/SegmentDropdown';
import { Chart } from './shared/Chart';
import { Download, Users, Brain, BarChart, AlertCircle, Info, Zap, Globe, Theater, Repeat, DollarSign, Droplet, Fish, Wallet, Bot, User, TrendingUp } from 'lucide-react';
// User segments
const segments = [{
  id: 'whales',
  label: 'Whales',
  description: 'Top 1% of spenders',
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-14385" />,
  algorithmCoverage: 92,
  fallbackCoverage: 8,
  arpu: 125.8,
  userCount: 1250,
  revenue: 157250,
  aiRevenue: 0,
  humanRevenue: 157250,
  customerClass: 'VIP'
}, {
  id: 'dolphins',
  label: 'Dolphins',
  description: 'Regular spenders',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-14386" />,
  algorithmCoverage: 85,
  fallbackCoverage: 15,
  arpu: 42.5,
  userCount: 8500,
  revenue: 361250,
  aiRevenue: 90313,
  humanRevenue: 270937,
  customerClass: 'Spender'
}, {
  id: 'minnows',
  label: 'Minnows',
  description: 'Low spenders',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-14387" />,
  algorithmCoverage: 72,
  fallbackCoverage: 28,
  arpu: 12.75,
  userCount: 22000,
  revenue: 280500,
  aiRevenue: 252450,
  humanRevenue: 28050,
  customerClass: 'Low Spender'
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  description: 'Never purchased',
  icon: <Wallet size={16} className="text-gray-500" data-id="element-14388" />,
  algorithmCoverage: 45,
  fallbackCoverage: 55,
  arpu: 0,
  userCount: 158000,
  revenue: 0,
  aiRevenue: 0,
  humanRevenue: 0,
  customerClass: 'Newbie'
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  description: 'Declining activity',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-14389" />,
  algorithmCoverage: 88,
  fallbackCoverage: 12,
  arpu: 18.25,
  userCount: 12500,
  revenue: 228125,
  aiRevenue: 205313,
  humanRevenue: 22812,
  customerClass: 'Dormant'
}];
// Fallback methods
const fallbackMethods = [{
  id: 'lookalike',
  label: 'Lookalike Audiences',
  description: 'Find similar users based on known whales/dolphins',
  icon: <Repeat size={16} className="text-indigo-500" data-id="element-14390" />
}, {
  id: 'broad',
  label: 'Broad Targeting',
  description: 'Run ads/offers to wide pool until algorithm gathers signals',
  icon: <Zap size={16} className="text-amber-500" data-id="element-14391" />
}, {
  id: 'context',
  label: 'Context Fallback',
  description: 'Device, GEO, time-of-day, minimal demographics',
  icon: <Globe size={16} className="text-blue-500" data-id="element-14392" />
}, {
  id: 'persona',
  label: 'Persona/Emotion Fallback',
  description: 'Only used when no other signals are available',
  icon: <Theater size={16} className="text-pink-500" data-id="element-14393" />
}];
// Chart data
const algorithmVsFallbackData = [{
  name: 'Whales',
  Algorithm: 92,
  Fallback: 8
}, {
  name: 'Dolphins',
  Algorithm: 85,
  Fallback: 15
}, {
  name: 'Minnows',
  Algorithm: 72,
  Fallback: 28
}, {
  name: 'Non-Spenders',
  Algorithm: 45,
  Fallback: 55
}, {
  name: 'Churn Risks',
  Algorithm: 88,
  Fallback: 12
}];
const rpmLiftData = [{
  name: 'Whales',
  Algorithm: 8.75,
  Fallback: 5.2
}, {
  name: 'Dolphins',
  Algorithm: 6.5,
  Fallback: 4.1
}, {
  name: 'Minnows',
  Algorithm: 4.25,
  Fallback: 2.8
}, {
  name: 'Non-Spenders',
  Algorithm: 0.85,
  Fallback: 0.45
}, {
  name: 'Churn Risks',
  Algorithm: 5.75,
  Fallback: 3.9
}];
// Revenue split data
const revenueSplitData = [{
  name: 'Whales',
  AI: 0,
  Human: 157250
}, {
  name: 'Dolphins',
  AI: 90313,
  Human: 270937
}, {
  name: 'Minnows',
  AI: 252450,
  Human: 28050
}, {
  name: 'Non-Spenders',
  AI: 0,
  Human: 0
}, {
  name: 'Churn Risks',
  AI: 205313,
  Human: 22812
}];
export const SegmentationHub: React.FC = () => {
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [segmentFallbacks, setSegmentFallbacks] = useState<Record<string, string>>(segments.reduce((acc, segment) => ({
    ...acc,
    [segment.id]: segment.id === 'non-spenders' ? 'broad' : 'lookalike'
  }), {}));
  const [exportSuccess, setExportSuccess] = useState(false);
  const handleFallbackChange = (segmentId: string, fallbackMethod: string) => {
    setSegmentFallbacks(prev => ({
      ...prev,
      [segmentId]: fallbackMethod
    }));
  };
  const handleExportData = () => {
    // Prepare the data for export
    const exportData = segments.map(segment => ({
      Segment: segment.label,
      'Customer Classification': segment.customerClass,
      Description: segment.description,
      Users: segment.userCount,
      ARPU: `$${segment.arpu.toFixed(2)}`,
      Revenue: `$${segment.revenue.toLocaleString()}`,
      'AI Revenue': `$${segment.aiRevenue.toLocaleString()}`,
      'Human Revenue': `$${segment.humanRevenue.toLocaleString()}`,
      'AI Commission Fee': `$${(segment.aiRevenue * 0.15).toLocaleString()}`,
      'Algorithm Coverage': `${segment.algorithmCoverage}%`,
      'Fallback Coverage': `${segment.fallbackCoverage}%`,
      'Fallback Method': segmentFallbacks[segment.id]
    }));
    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [headers.join(','), ...exportData.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))].join('\n');
    // Create a blob and download link
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'segment_data_with_ai_human_revenue.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Show success message
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };
  const totalUsers = segments.reduce((sum, segment) => sum + segment.userCount, 0);
  const totalRevenue = segments.reduce((sum, segment) => sum + segment.revenue, 0);
  const totalAiRevenue = segments.reduce((sum, segment) => sum + segment.aiRevenue, 0);
  const totalHumanRevenue = segments.reduce((sum, segment) => sum + segment.humanRevenue, 0);
  const aiPercentage = Math.round(totalAiRevenue / totalRevenue * 100);
  const humanPercentage = 100 - aiPercentage;
  // Calculate AI commission
  const aiCommissionRate = 0.15; // 15% commission rate for AI
  const aiCommission = totalAiRevenue * aiCommissionRate;
  const avgAlgorithmCoverage = segments.reduce((sum, segment) => sum + segment.algorithmCoverage, 0) / segments.length;
  const avgFallbackCoverage = segments.reduce((sum, segment) => sum + segment.fallbackCoverage, 0) / segments.length;
  return <div data-id="element-14394">
      <div className="flex items-center justify-between mb-6" data-id="element-14395">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-14396">
          Universal Segmentation Hub
        </h2>
        <div className="flex items-center gap-3" data-id="element-14397">
          <button onClick={handleExportData} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-14398">
            <Download size={16} data-id="element-14399" />
            <span data-id="element-14400">Export Segment Data</span>
          </button>
          {exportSuccess && <div className="absolute top-4 right-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-md shadow-md" data-id="element-14401">
              Data exported successfully!
            </div>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-14402">
        <StatsCard title="Total Users" value={totalUsers.toLocaleString()} change={8.2} changeLabel="vs last month" icon={<Users size={16} className="text-blue-500" data-id="element-14404" />} data-id="element-14403" />
        <StatsCard title="Total Revenue" value={`$${totalRevenue.toLocaleString()}`} change={12.5} changeLabel="vs last month" className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-green-900/20" data-id="element-14405" />
        <StatsCard title="Algorithm Coverage" value={`${avgAlgorithmCoverage.toFixed(1)}%`} change={5.8} changeLabel="vs last month" icon={<Brain size={16} className="text-purple-500" data-id="element-14407" />} className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20" data-id="element-14406" />
        <StatsCard title="Fallback Usage" value={`${avgFallbackCoverage.toFixed(1)}%`} change={-5.8} changeLabel="vs last month" icon={<Repeat size={16} className="text-indigo-500" data-id="element-14409" />} className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20" data-id="element-14408" />
      </div>

      {/* Revenue Split and AI Commission Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-14410">
        <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20 p-4 rounded-lg shadow-sm" data-id="element-14411">
          <div className="flex justify-between items-start" data-id="element-14412">
            <div data-id="element-14413">
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center group relative" data-id="element-14414">
                Revenue Split (AI vs Human)
                <Info size={14} className="ml-1 text-gray-400 cursor-help" data-id="element-14415" />
                <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-white dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-14416">
                  AI revenue is generated via automated chat, upsells, and
                  campaigns. Human revenue is generated by creators or
                  moderators.
                </span>
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-14417">
                {aiPercentage}% / {humanPercentage}%
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-14418">
                ${totalAiRevenue.toLocaleString()} / $
                {totalHumanRevenue.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-14419">
              <Zap className="w-5 h-5 text-purple-500 dark:text-purple-400" data-id="element-14420" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-14421">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-14422" />
            <span className="text-green-500 font-medium" data-id="element-14423">11.2%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-14424">
              AI growth vs last month
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 p-4 rounded-lg shadow-sm" data-id="element-14425">
          <div className="flex justify-between items-start" data-id="element-14426">
            <div data-id="element-14427">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-14428">
                AI Commission Fee
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-14429">
                ${aiCommission.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-14430">
                Free: 15%, $30: 10%, $100: 8.5%
              </p>
            </div>
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg" data-id="element-14431">
              <DollarSign className="w-5 h-5 text-indigo-500 dark:text-indigo-400" data-id="element-14432" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-14433">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-14434" />
            <span className="text-green-500 font-medium" data-id="element-14435">9.5%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-14436">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800 p-4 mb-6" data-id="element-14437">
          <div className="flex items-start" data-id="element-14438">
            <Zap className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-14439" />
            <div data-id="element-14440">
              <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-14441">
                Hybrid Routing Logic
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-14442">
                AI handles all new and low-value customers. After $120 lifetime
                spend, customer is routed to Human operator for personalized
                attention. This threshold is configurable per Angel.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-14443">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14444">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14445">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14446">
              <Brain size={18} className="text-purple-500 mr-2" data-id="element-14447" />
              Algorithm vs Fallback Coverage by Segment
            </h3>
          </div>
          <div className="p-6" data-id="element-14448">
            <div className="h-80" data-id="element-14449">
              <Chart title="" data={algorithmVsFallbackData} type="bar" height={300} yAxisLabel="Percentage (%)" data-id="element-14450" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14451">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14452">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14453">
              <BarChart size={18} className="text-green-500 mr-2" data-id="element-14454" />
              RPM Lift: Algorithm vs Fallback
            </h3>
          </div>
          <div className="p-6" data-id="element-14455">
            <div className="h-80" data-id="element-14456">
              <Chart title="" data={rpmLiftData} type="bar" height={300} yAxisLabel="Revenue Per Mille ($)" data-id="element-14457" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Split Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14458">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14459">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14460">
            <BarChart size={18} className="text-purple-500 mr-2" data-id="element-14461" />
            Revenue Split by Segment (AI vs Human)
          </h3>
        </div>
        <div className="p-6" data-id="element-14462">
          <div className="h-80" data-id="element-14463">
            <Chart title="" data={revenueSplitData} type="bar" height={300} yAxisLabel="Revenue ($)" data-id="element-14464" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14465">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14466">
          <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-14467">
            Segment Fallback Configuration
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-14468">
          <table className="w-full" data-id="element-14469">
            <thead data-id="element-14470">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-14471">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14472">
                  Segment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14473">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14474">
                  ARPU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14475">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14476">
                  AI Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14477">
                  Human Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14478">
                  AI Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14479">
                  Algorithm Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14480">
                  Fallback Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14481">
                  Fallback Method
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-14482">
              {segments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-14483">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14484">
                    <div className="flex items-center" data-id="element-14485">
                      <div className="flex-shrink-0" data-id="element-14486">
                        <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-14487">
                          {segment.icon}
                        </div>
                      </div>
                      <div className="ml-3" data-id="element-14488">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14489">
                          {segment.label}
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-14490">
                            {segment.customerClass}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-14491">
                          {segment.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14492">
                    {segment.userCount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14493">
                    ${segment.arpu.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14494">
                    ${segment.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14495">
                    <div className="flex items-center" data-id="element-14496">
                      <Bot className="w-4 h-4 text-indigo-500 mr-1" data-id="element-14497" />
                      <span data-id="element-14498">${segment.aiRevenue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14499">
                    <div className="flex items-center" data-id="element-14500">
                      <User className="w-4 h-4 text-green-500 mr-1" data-id="element-14501" />
                      <span data-id="element-14502">${segment.humanRevenue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14503">
                    ${(segment.aiRevenue * aiCommissionRate).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14504">
                    <div className="flex items-center" data-id="element-14505">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-14506">
                        <div className="h-full bg-purple-500 rounded-full" style={{
                      width: `${segment.algorithmCoverage}%`
                    }} data-id="element-14507"></div>
                      </div>
                      <span className="ml-2" data-id="element-14508">{segment.algorithmCoverage}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14509">
                    <div className="flex items-center" data-id="element-14510">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-14511">
                        <div className="h-full bg-indigo-500 rounded-full" style={{
                      width: `${segment.fallbackCoverage}%`
                    }} data-id="element-14512"></div>
                      </div>
                      <span className="ml-2" data-id="element-14513">{segment.fallbackCoverage}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14514">
                    <select value={segmentFallbacks[segment.id]} onChange={e => handleFallbackChange(segment.id, e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-14515">
                      {fallbackMethods.map(method => <option key={method.id} value={method.id} data-id="element-14516">
                          {method.label}
                        </option>)}
                    </select>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14517">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14518">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14519">
            <Info size={18} className="text-blue-500 mr-2" data-id="element-14520" />
            Fallback Methods
          </h3>
        </div>
        <div className="p-6" data-id="element-14521">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-14522">
            {fallbackMethods.map(method => <div key={method.id} className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4" data-id="element-14523">
                <div className="flex items-start" data-id="element-14524">
                  <div className="flex-shrink-0 mt-1" data-id="element-14525">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-800 flex items-center justify-center" data-id="element-14526">
                      {method.icon}
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-14527">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14528">
                      {method.label}
                    </h4>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-14529">
                      {method.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};